import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Message, Segment } from 'semantic-ui-react';
import { GREY_COLOR } from 'src/app.constants';
import { BomGrid } from 'src/components/BomModal';
import CustomAccordion from 'src/components/controls/CustomAccordion';
import { PanelHeading } from 'src/components/PanelHeading';
import additionalMessages from '../../messages';
import { useBomLoader, SECTION_KEYS } from './useBomLoader';

// Each entry describes one accordion panel. `getRowData` extracts the relevant
// array from the section's cached data object.
const ACCORDION_ITEMS = [
  {
    key: 'PTPBom',
    titleId: 'common.ptpBom',
    defaultMessage: 'PTP BOM',
    section: SECTION_KEYS.PTP,
    accordionTitle: 'ptp_bom',
    getRowData: (d) => d?.ptp_bom,
  },
  {
    key: 'PMPNetworkBom',
    titleId: 'common.pmpNetworkBom',
    defaultMessage: 'PMP Network BOM',
    section: SECTION_KEYS.PMP,
    accordionTitle: 'pmp_network_bom',
    getRowData: (d) => d?.ap_bom,
  },
  {
    key: 'PMPSubscribersBom',
    titleId: 'common.pmpSubscribersBom',
    defaultMessage: 'PMP Subscriber Modules BOM',
    section: SECTION_KEYS.PMP,
    accordionTitle: 'pmp_subscriber_modules_bom',
    getRowData: (d) => d?.sm_bom,
  },
];

function BomPanel(props) {
  const { formatMessage } = props.intl;
  const {
    projectId,
    projectName,
    accessPoints,
    ptpLinksCount,
    accessPointsCount,
    subscriberSiteCount,
  } = useSelector((state) => state.mainFrame);
  const networkBomReadyTick = useSelector(
    (state) => state.mainFrame.networkBomReadyTick
  );

  const anyDirtyAPs = accessPoints?.features.some(
    (f) => f.properties.strokeColor === GREY_COLOR
  );
  const { created } = useSelector((state) => state?.bestServer);
  const isCalculating = anyDirtyAPs || created !== null;

  const hasPmpData = accessPointsCount > 0 || subscriberSiteCount > 0;
  const hasPtpData = ptpLinksCount > 0;

  const { sectionData, loading, readyState } = useBomLoader({
    projectId,
    hasPmpData,
    hasPtpData,
    isCalculating,
    networkBomReadyTick,
  });

  const accordions = ACCORDION_ITEMS.map(
    ({
      key,
      titleId,
      defaultMessage,
      section,
      accordionTitle,
      getRowData,
    }) => ({
      key,
      titleProps: {
        content: (
          <FormattedMessage id={titleId} defaultMessage={defaultMessage} />
        ),
      },
      contentProps: {
        content: (
          <Segment loading={loading && !readyState[section]}>
            <BomGrid
              accordionTitle={accordionTitle}
              rowData={getRowData(sectionData[section]) ?? null}
              autoLoad={false}
              editable={false}
            />
          </Segment>
        ),
      },
    })
  );

  return (
    <div
      className="panel-with-heading"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        rowGap: '1rem',
      }}
    >
      <PanelHeading
        title={
          <>
            {formatMessage(additionalMessages.bom)} for {projectName}
          </>
        }
      />
      {isCalculating && (
        <Message
          warning
          style={{ margin: '0', flexShrink: '0', flexBasis: '3rem' }}
        >
          <Message.Header>Calculating. Please wait...</Message.Header>
        </Message>
      )}

      <div
        className="main-panel"
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        <CustomAccordion
          accordions={accordions}
          accordionProps={{
            style: { height: '100%', overflowY: 'auto', padding: '0.8rem' },
          }}
        />
      </div>
    </div>
  );
}

export default injectIntl(BomPanel);
